import SubHeaders from '@/components/Headers/SubHeaders'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { paths } from '@/utils/path'
import { FileImage, Loader2, Trash2 } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'
import { useConvertWebp } from '../../hooks/use-convert-webp'
import FileDropZone from './file-drop-zone'

const ConvertWebp = () => {
    const [files, setFiles] = useState<File[]>([])
    const [quality, setQuality] = useState(80)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const { mutateAsync, isPending } = useConvertWebp()

    const addFiles = useCallback((newFiles: FileList | File[]) => {
        setFiles((prev) => {
            const unique = Array.from(newFiles).filter(
                (f) => !prev.some((p) => p.name === f.name && p.size === f.size)
            )
            return [...prev, ...unique]
        })
    }, [])

    const removeFile = useCallback((index: number) => {
        setFiles((prev) => prev.filter((_, i) => i !== index))
    }, [])

    const handleConvert = async () => {
        if (files.length === 0) return
        try {
            await mutateAsync({ files, quality })
        } catch {
            // Error handled by hook's onError
        }
    }

    const formatSize = (bytes: number) => {
        if (bytes < 1024) return `${bytes} B`
        if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`
        return `${(bytes / 1048576).toFixed(1)} MB`
    }

    return (
        <div>
            <SubHeaders
                breadcrumbs={[
                    { title: 'Tiện ích', path: paths.toolsConvertWebp },
                    {
                        title: 'Chuyển ảnh sang WebP',
                        path: paths.toolsConvertWebp,
                    },
                ]}
            />
            <div className="space-y-4">
                <Card>
                    <CardContent className="p-4 space-y-4">
                        <FileDropZone
                            fileInputRef={fileInputRef}
                            onFiles={addFiles}
                            onSelect={(e) => {
                                if (e.target.files) {
                                    addFiles(e.target.files)
                                    e.target.value = ''
                                }
                            }}
                        />

                        {/* File list */}
                        {files.length > 0 && (
                            <div className="space-y-2">
                                {files.map((file, index) => (
                                    <div
                                        key={`${file.name}-${file.size}`}
                                        className="flex items-center justify-between rounded-md border px-3 py-2"
                                    >
                                        <div className="flex items-center gap-2 min-w-0">
                                            <FileImage className="h-4 w-4 shrink-0 text-muted-foreground" />
                                            <span className="text-sm truncate">
                                                {file.name}
                                            </span>
                                            <span className="text-xs text-muted-foreground shrink-0">
                                                ({formatSize(file.size)})
                                            </span>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => removeFile(index)}
                                            className="h-7 w-7"
                                        >
                                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Quality slider */}
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <label className="text-sm font-medium">
                                    Chất lượng
                                </label>
                                <div className="flex items-center gap-1">
                                    <input
                                        type="number"
                                        min={1}
                                        max={100}
                                        value={quality}
                                        onChange={(e) =>
                                            setQuality(
                                                Math.min(
                                                    100,
                                                    Math.max(
                                                        1,
                                                        Number(
                                                            e.target.value
                                                        ) || 1
                                                    )
                                                )
                                            )
                                        }
                                        className="w-14 text-center text-sm border border-input rounded-md px-2 py-1 bg-background focus:outline-none focus:ring-1 focus:ring-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                    />
                                    <span className="text-sm text-muted-foreground">
                                        %
                                    </span>
                                </div>
                            </div>
                            <input
                                type="range"
                                min={1}
                                max={100}
                                value={quality}
                                onChange={(e) =>
                                    setQuality(Number(e.target.value))
                                }
                                className="w-full accent-primary"
                            />
                            <div className="flex justify-between text-xs text-muted-foreground">
                                <span>Nhỏ hơn</span>
                                <span>Chất lượng cao</span>
                            </div>
                        </div>

                        <Button
                            onClick={handleConvert}
                            disabled={files.length === 0 || isPending}
                            className="w-full"
                        >
                            {isPending ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <FileImage className="h-4 w-4" />
                            )}
                            {isPending
                                ? 'Đang chuyển đổi...'
                                : `Chuyển đổi sang WebP (${files.length})`}
                        </Button>
                    </CardContent>
                </Card>
            </div>
        </div>
    )
}

export default ConvertWebp
